import React, { useEffect, useState, memo } from "react";
import { Box, Card, CardContent, Grid, Typography, Button, Stack, Paper, TextField, Chip, Dialog, DialogContent, DialogTitle, DialogActions, FormControl } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth, useLoading, useNavigation, useSnackbar } from "../../Context/Contexts";
import { useTranslation } from "react-i18next";
import { BatchSummaryViewModel, ListQuery } from "../../Types/ExportTypes";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import ResponsivePagination from "../../components/ResponsivePagination";
import {
    LocationAutoComplete
} from "../../components/AutoComplete/AutoCompletes";
const BatchListPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {

    const { authService } = useAuth();
    const { showLoading, hideLoading, isLoading } = useLoading();
    const { navigate } = useNavigation();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [batches, setBatches] = useState<BatchSummaryViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 6,
        filter: "",
        filters: [],
    });
    const [filter, setFilter] = useState<string>("");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const [isCreateBatchModalOpen, setIsCreateBatchModalOpen] = useState(false);
    const fetchItems = async () => {
        showLoading();
        try {
            const response = await authService.post<{ items: BatchSummaryViewModel[]; count: number }>(
                "/batchApi/BatchList",
                query
            );

            setTotalCount(response.count);
            setBatches(response.items);
        } finally {
            hideLoading();
        }
    };


    useEffect(() => {
        fetchItems();
    }, [query]);


    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (filter) {
            const newFilter = buildQueryFilter(filterProperty, filter);
            const newFilters = [...(query.filters ?? []), newFilter];
            setQuery({ ...query, filters: newFilters, page: 0 });
            setFilter("");
        } else {
            setQuery({ ...query, filters: query.filters, page: 0 });
        }
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const openBatch = (batchId?: number | null) => {
        if (!batchId) return;
        navigate({ path: "warehouse/batch/edit", params: { batchId } });
    };

    const openFirstOrder = async (batchId?: number | null, toPack?: boolean) => {
        if (!batchId) return;
        const res = await authService.get<{ orderNo: string | null }>(`/batchApi/FirstOrder?batchId=${batchId}`);
        if (res.orderNo) {
            navigate({ path: toPack ? "warehouse/pack/edit" : "warehouse/pick/edit", params: { orderNo: res.orderNo } });
        } else {
            showSnackbar(t("common.noOrderInBatch"), "info");
        }
    };
    const handleCreate = async (description?: string, location?: string) => {
        if (!description && !location) {
            setIsCreateBatchModalOpen(true);
            return;
        }
        setIsCreateBatchModalOpen(false);
        showLoading();
        try {
            var response = await authService.post<{ batchId: number }>(`/batchApi/CreateBatch`, { description, location });
            showSnackbar(t("dialog.savedSuccessfully"), "success");
            hideLoading();
            var batchId = response.batchId;
            navigate({ path: "warehouse/batch/edit", params: { batchId } });
        } catch (error: any) {
            showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

        } finally {
            hideLoading();
        }
    }

    type CreateBatchModalprops = {
        open: boolean;
        onClose: () => void;
        onConfirm: (description: string, location: string) => void;
    };
    const CreateBatchModal = memo(({ open, onClose, onConfirm }: CreateBatchModalprops) => {
        const [description, setDescription] = useState("");
        const [location, setLocation] = useState("");
        return (
            <Dialog open={open} onClose={onClose}>
                <DialogTitle>{t("common.create")}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t("common.description")}
                        fullWidth
                        variant="outlined"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)} // Only updates local state
                        error={!description.trim()}
                        helperText={!description.trim() ? t("dialog.descriptionRequired") : ""}
                    />
                    <FormControl fullWidth margin="normal">
                        <LocationAutoComplete
                            key={location}
                            valueId={location}
                            onChange={(selected) => setLocation(selected?.value || "")}
                            infoText={t("help.locationHelp")}
                            error={!location.trim()}
                            helperText={!location.trim() ? t("dialog.locationRequired") : ""}
                        />
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={() => {
                            if (!description.trim()) {
                                showSnackbar(t("dialog.codeEmpty"), "warning")
                                return;
                            }
                            if (!location.trim()) {
                                showSnackbar(t("dialog.locationRequired"), "warning")
                                return;
                            }
                            onConfirm(description?.trim(), location?.trim());
                        }}
                        color="primary"
                    >
                        {t("common.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    });

    return (
        <CenteredListCard title="Batch">
            <CreateBatchModal open={isCreateBatchModalOpen} onClose={() => setIsCreateBatchModalOpen(false)} onConfirm={handleCreate} />
         
                <Box sx={{ marginBottom: 2 }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => handleCreate()}
                    >
                        {t("common.create")}
                    </Button>
                </Box>
                <Box
                    component="form"
                    onSubmit={handleSearchSubmit}
                    sx={{
                        display: "flex",
                        gap: 2,
                        marginBottom: 2,
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={5} lg={2}>
                            <TextField
                                select
                                label={t("common.field")}
                                value={filterProperty}
                                onChange={(e) => setFilterProperty(e.target.value)}
                                SelectProps={{ native: true }}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            >
                                <option value="">{t("common.default")}</option>
                                <option value="ext_Id">{t("common.number")}</option>
                                <option value="ext_Description">{t("common.description")}</option>
                            </TextField>
                        </Grid>
                        <Grid item xs={7} lg={7}>
                            <TextField
                                label={t("common.search")}
                                variant="outlined"
                                fullWidth
                                onChange={(event) => setFilter(event.target.value)}
                                value={filter}
                                disabled={isLoading}
                            />
                        </Grid>
                        <Grid item xs={12} lg={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={isLoading}
                            >
                                {t("common.search")}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    {query.filters?.map((f, index) => (
                        <Chip
                            key={index}
                            label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                            onDelete={() => {
                                const newFilters = query.filters?.filter((_, i) => i !== index);
                                setQuery({ ...query, filters: newFilters, page: 0 });
                            }}
                            deleteIcon={<DeleteIcon />}
                        />
                    ))}
                </Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {batches.map((batch) => (
                        <Grid item
                            xs={12}
                            sm={batches.length === 1 ? 12 : batches.length === 2 ? 6 : 6}
                            md={batches.length === 1 ? 12 : batches.length === 2 ? 6 : 4}
                            key={batch.batchId}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                        {batch.batchId} - {batch.batchDescription}
                                    </Typography>
                                    <Typography variant="body2">
                                        {t("common.orders")}: {batch.batchOrderCount}
                                    </Typography>
                                    <Typography variant="body2">
                                        {t("common.lines")}: {batch.batchLineCount}
                                    </Typography>
                                    <Box sx={{ marginTop: 2, textAlign: "center" }}>
                                        <Button fullWidth sx={{ mb: 2 }} variant="contained" onClick={() => openBatch(batch.batchId)}>{t("common.openBatch")}</Button>
                                        <Button fullWidth color="info" sx={{ mb: 2 }} variant="contained" onClick={() => openFirstOrder(batch.batchId, false)}>{t("common.openPick")}</Button>
                                        <Button fullWidth color="success" variant="contained" onClick={() => openFirstOrder(batch.batchId, true)}>{t("common.openPack")}</Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {batches.length === 0 && (
                        <Grid item xs={12}>
                            <Typography align="center">{t("common.noEntriesFound")}</Typography>
                        </Grid>
                    )}
                </Grid>
                <ResponsivePagination

                    page={query.page}

                    pageSize={query.pageSize}

                    totalCount={totalCount}

                    onPageChange={handlePageChange}

                />
 
        </CenteredListCard>
    );
};

export default BatchListPage;